import { useCallback, useRef, useState } from 'react'

import { QueryErrorResetBoundary } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'

import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react'
import {
	AlertTriangle,
	ArrowLeft,
	Check,
	Copy,
	LogIn,
	RefreshCw,
	Search,
	ShieldAlert,
	WifiOff,
} from 'lucide-react'

import { isDEV, isDEVDeploy } from '@/lib/constants'

import { useErrorBoundary } from '@/hooks/use-error-boundary'
import { cn } from '@/utils/ui/styles'

import {
	isApiError,
	isAuthError,
	isForbiddenError,
	isNetworkError,
	isNotFoundError,
	isServerError,
} from '@/shared/api/utils/error'
import { useMaybeCurrentTenant } from '@/shared/auth/store'
import { ErrorContextProvider } from '@/shared/error-context/provider'

import { LoadingButton } from '../buttons/loading-button'
import { Button } from '../ui/button'
import {
	Card,
	CardContent,
	CardFooter,
	CardHeader,
	CardTitle,
} from '../ui/card'

import type { PropsWithChildren, ReactNode } from 'react'

export type ErrorBoundaryFallbackProps = {
	error?: unknown
	reset: () => void
}

export interface ErrorBoundaryProps extends PropsWithChildren {
	/**
	 * Feature name for error categorization in Sentry
	 * @example 'playbooks' | 'integrations' | 'alerts'
	 */
	feature?: string

	/**
	 * Sub-section within the feature for finer error context
	 * @example 'detail' | 'list' | 'settings'
	 */
	section?: string

	/**
	 * Entity ID relevant to the error context
	 * @example alert ID, playbook ID
	 */
	entityId?: string

	/**
	 * Arbitrary metadata attached to error context
	 */
	metadata?: Record<string, string | number | boolean>

	/**
	 * Custom fallback component. If not provided, uses the default error UI.
	 * The component receives error object and reset function.
	 */
	fallback?: ({ error, reset }: ErrorBoundaryFallbackProps) => ReactNode

	/**
	 * Custom error title for the default fallback UI
	 * @default 'Oops! Something went wrong'
	 */
	fallbackTitle?: string

	/**
	 * Custom error message for the default fallback UI
	 * @default 'We encountered an unexpected error while processing your request.'
	 */
	fallbackMessage?: string

	/**
	 * Additional class names for the default fallback container
	 */
	className?: string

	/**
	 * Whether to show the error code in the default fallback UI
	 * @default true
	 */
	showErrorCode?: boolean
}

type ErrorVariant =
	| 'network'
	| 'server'
	| 'auth'
	| 'forbidden'
	| 'not-found'
	| 'generic'

function getErrorVariant(error: unknown): ErrorVariant {
	if (isNetworkError(error)) return 'network'
	if (isServerError(error)) return 'server'
	if (isForbiddenError(error)) return 'forbidden'
	if (isAuthError(error)) return 'auth'
	if (isNotFoundError(error)) return 'not-found'
	return 'generic'
}

const variantConfig: Record<
	ErrorVariant,
	{
		icon: typeof AlertTriangle
		title: string
		message: string
		showRetry: boolean
		showCorrelationId: boolean
	}
> = {
	'network': {
		icon: WifiOff,
		title: 'Connection issue',
		message: 'Check your internet connection and try again.',
		showRetry: true,
		showCorrelationId: false,
	},
	'server': {
		icon: AlertTriangle,
		title: 'Something went wrong',
		message: 'We encountered a server error. Please try again later.',
		showRetry: true,
		showCorrelationId: true,
	},
	'auth': {
		icon: LogIn,
		title: 'Session expired',
		message: 'Your session has expired. Please sign in again.',
		showRetry: false,
		showCorrelationId: false,
	},
	'forbidden': {
		icon: ShieldAlert,
		title: 'Access denied',
		message: 'You do not have permission to access this resource.',
		showRetry: false,
		showCorrelationId: false,
	},
	'not-found': {
		icon: Search,
		title: 'Not found',
		message: 'The requested resource could not be found.',
		showRetry: false,
		showCorrelationId: false,
	},
	'generic': {
		icon: AlertTriangle,
		title: 'Oops! Something went wrong',
		message:
			'We encountered an unexpected error while processing your request.',
		showRetry: true,
		showCorrelationId: true,
	},
}

/**
 * Default error fallback UI component.
 * Differentiates UI based on error type (network, server, auth, 403, 404, generic).
 */
export function DefaultErrorFallback({
	error,
	resetError,
	title,
	message,
	showErrorCode = true,
	className,
	correlationId,
}: {
	error: unknown
	resetError: () => void
	title?: string
	message?: string
	showErrorCode?: boolean
	className?: string
	correlationId?: string
}) {
	const [isLoading, setIsLoading] = useState(false)
	const [retryCount, setRetryCount] = useState(0)
	const [copied, setCopied] = useState(false)
	const { reset: resetQuery } = useErrorBoundary()

	if (isDEV || isDEVDeploy) {
		console.error(error)
	}

	const variant = getErrorVariant(error)
	const config = variantConfig[variant]
	const Icon = config.icon

	const handleReset = useCallback(() => {
		setIsLoading(true)
		setRetryCount((c) => c + 1)
		resetError()
		resetQuery()
		// If the error re-occurs, the boundary re-catches and re-renders this fallback.
		// isLoading will be reset because the component remounts with fresh state.
		// If reset succeeds, this component unmounts entirely.
	}, [resetError, resetQuery])

	const handleCopyCorrelationId = useCallback(() => {
		if (!correlationId) return
		void navigator.clipboard.writeText(correlationId)
		setCopied(true)
		setTimeout(() => setCopied(false), 2000)
	}, [correlationId])

	const displayTitle = title || config.title
	const displayMessage = message || config.message
	const showRef = showErrorCode && config.showCorrelationId && correlationId

	return (
		<div
			className={cn(
				'bg-background flex min-h-100 items-center justify-center',
				className,
			)}
		>
			<Card className="w-full max-w-md">
				<CardHeader className="flex flex-col items-center space-y-2">
					<Icon className="text-destructive-text size-12" />
					<CardTitle className="text-center text-2xl font-bold">
						{displayTitle}
					</CardTitle>
				</CardHeader>
				<CardContent className="text-center">
					<p className="text-muted-foreground">{displayMessage}</p>
					{retryCount >= 2 && config.showRetry && (
						<p className="text-muted-foreground mt-2 text-sm font-medium">
							Still experiencing issues. If this persists, contact support
							{showRef ? ' with the reference below' : ''}.
						</p>
					)}
					{showRef && (
						<div className="mt-3 flex items-center justify-center gap-1.5">
							<p className="text-muted-foreground text-sm">
								Reference: {correlationId.slice(0, 8).toUpperCase()}
							</p>
							<Button
								variant="ghost"
								size="icon"
								className="h-6 w-6"
								onClick={handleCopyCorrelationId}
							>
								{copied ? (
									<Check className="h-3 w-3" />
								) : (
									<Copy className="h-3 w-3" />
								)}
							</Button>
						</div>
					)}
				</CardContent>
				<CardFooter className="flex justify-center space-x-4">
					{config.showRetry && (
						<LoadingButton isLoading={isLoading} onClick={handleReset}>
							<RefreshCw className="mr-1.5 h-4 w-4" />
							Try Again
						</LoadingButton>
					)}
					{variant === 'auth' && (
						<Button nativeButton={false} render={<Link to="/login" />}>
							<LogIn className="mr-1.5 h-4 w-4" />
							Go to Login
						</Button>
					)}
					{(variant === 'forbidden' || variant === 'not-found') && (
						<Button variant="outline" onClick={() => window.history.back()}>
							<ArrowLeft className="mr-1.5 h-4 w-4" />
							Go Back
						</Button>
					)}
					{config.showRetry && (
						<Button
							variant="outline"
							nativeButton={false}
							render={<Link to="/home" />}
						>
							Go to Homepage
						</Button>
					)}
				</CardFooter>
			</Card>
		</div>
	)
}

/**
 * Unified Error Boundary Component
 *
 * - Wraps children with ErrorContextProvider for structured error context
 * - Generates correlation IDs (UUID) for error tracking
 * - Differentiates error UI by type (network, server, auth, 403, 404)
 * - Automatically resets React Query on error reset
 * - Reports errors to Sentry with rich context and tags
 * - Provides customizable fallback UI
 *
 * @example
 * ```tsx
 * <ErrorBoundary feature="playbooks" section="detail" entityId={playbookId}>
 *   <PlaybookDetail />
 * </ErrorBoundary>
 * ```
 */
export function ErrorBoundary({
	children,
	feature = 'unknown',
	section,
	entityId,
	metadata,
	fallback,
	fallbackTitle,
	fallbackMessage,
	className,
	showErrorCode = true,
}: ErrorBoundaryProps) {
	const tenant = useMaybeCurrentTenant()
	const correlationIdRef = useRef('')

	return (
		<QueryErrorResetBoundary key={tenant ?? 'no-tenant'}>
			{({ reset: resetQuery }) => (
				<SentryErrorBoundary
					onReset={resetQuery}
					fallback={({ error, resetError }) => {
						if (fallback) {
							const customFallback = fallback({
								error,
								reset: () => {
									resetError()
									resetQuery()
								},
							})
							return <>{customFallback}</>
						}

						return (
							<DefaultErrorFallback
								error={error}
								resetError={() => {
									resetError()
									resetQuery()
								}}
								title={fallbackTitle}
								message={fallbackMessage}
								showErrorCode={showErrorCode}
								className={className}
								correlationId={correlationIdRef.current}
							/>
						)
					}}
					beforeCapture={(scope, error) => {
						correlationIdRef.current = crypto.randomUUID()

						scope.setTag('feature', feature)
						scope.setTag('tenant', tenant || 'unknown')
						scope.setTag('errorSource', 'component')
						scope.setTag('correlationId', correlationIdRef.current)
						scope.setTag('caughtByBoundary', 'true')

						if (section) scope.setTag('section', section)
						if (entityId) scope.setTag('entityId', entityId)

						scope.setContext('errorBoundary', {
							feature,
							section,
							entityId,
							metadata,
							tenant,
							correlationId: correlationIdRef.current,
							fallbackTitle,
							fallbackMessage,
							hasCustomFallback: !!fallback,
						})

						// Fingerprint boundary-caught API errors to group by feature + status
						if (isApiError(error)) {
							scope.setFingerprint([
								'boundary-api-error',
								feature,
								String(error.status ?? 'unknown'),
							])
						}
					}}
				>
					<ErrorContextProvider
						feature={feature}
						section={section}
						entityId={entityId}
						metadata={metadata}
					>
						{children}
					</ErrorContextProvider>
				</SentryErrorBoundary>
			)}
		</QueryErrorResetBoundary>
	)
}
